import React, { memo, useCallback, Fragment, useEffect } from 'react';
import { ScenarioDrillWidgetForm, MultipleChoiceTestBlockVariation, ScenarioForm } from './MultipleChoiceTestBlock';
import { useObservable } from '@core/utils/hooks/rxjs';
import { Label, Row, Col } from 'reactstrap';
import { SortableList } from '@modules/common/Sortable';
import { ScenarioDrillItem } from './MultipleChoiceTestBlockItem';
import { SortableElement, SortableHandle, SortEnd } from 'react-sortable-hoc';
import { R } from '@core/utils/r';
import { FormHTML } from '@core/components/form/FormHTML';
import { Button } from '@core/components/Button';

export interface ScenarioDrillBlockSettingsProps {
  form: ScenarioDrillWidgetForm;
  variation: MultipleChoiceTestBlockVariation;
}

export const MultipleChoiceTestBlockSettings = memo((props: ScenarioDrillBlockSettingsProps) => {
  const { form, variation } = props;
  const scenarios = useObservable(form.controls.scenarios.items$, []);
  const scenarioValues = useObservable(form.controls.scenarios.value$, []);

  useEffect(() => {
    if (scenarios.length === 0) {
      form.controls.scenarios.push();
    }
  }, [form, scenarios]);

  const onSortEnd = useCallback(
    (sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;
      const newScenarios = R.move(scenarioValues, oldIndex, newIndex);
      form.patchValue({
        scenarios: newScenarios,
      });
    },
    [form, scenarioValues],
  );

  return (
    <div>
      <Fragment>
        <div className="d-flex" style={{ justifyContent: 'space-between' }}>
          {!!scenarios.length && <Label>Multiple Choice Question</Label>}
          <Button
            extraClassNames="mb-1"
            onClick={() => {
              form.controls.scenarios.push();
            }}
          >
            Add Question
          </Button>
        </div>
        <SortableList lockAxis="y" distance={2} onSortEnd={sort => onSortEnd(sort)} useDragHandle={true} helperClass="z5000">
          {scenarios.map((ctrl, index) => {
            return <SortableScenarioItem key={index} index={index} ctrl={ctrl} form={form} i={index} />;
          })}
        </SortableList>
        <Row>
          <Col md={12}>
            <FormHTML control={form.controls.feedback} minimal={true} />
          </Col>
        </Row>
      </Fragment>
    </div>
  );
});

interface ScenarioItemProps {
  i: number;
  ctrl: ScenarioForm;
  form: ScenarioDrillWidgetForm;
}

const ScenarioItem = memo((props: ScenarioItemProps) => {
  const { ctrl, form, i } = props;
  const DragHandle = SortableHandle(() => <i className="fas fa-grip-vertical" style={{ cursor: 'move' }}></i>);

  return (
    <div>
      <Row key={`question-${i} mb-2 mt-2`}>
        <Col md={11}>
          <FormHTML control={ctrl.controls.scenario} minimal={true} />
        </Col>
        <Col md={1}>
          {!!i && (
            <a className="btn btn-outline-danger btn-sm" onClick={() => form.controls.scenarios.removeAt(i)}>
              <i className="bx bx-x"></i>
            </a>
          )}
        </Col>
      </Row>
      <ScenarioDrillItem control={ctrl} />
      <hr />
    </div>
  );
});

export const SortableScenarioItem = SortableElement(ScenarioItem);
